import React, { useRef, useState, useMemo, useEffect } from "react";
import {
   Box,
   Button,
   Card,
   Flex,
   Form,
   H1,
   H2,
   H3,
   Page,
   Spinner,
   Table,
   Typography,
   useI18nContext,
   P,
   Switch,
   FlexList,
   Required,
   Select,
} from "@procore/core-react";
import { isBefore } from "date-fns";
import {
   ProjectDetailAlertTokens,
   scheduleAlertHourOptions,
   scheduleAlertMinuteOptions,
} from "../../communications/constants";
import type { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { Ban, Check } from "@procore/core-icons";
import { useGetPeopleAlertConfig } from "./queries";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useGetPeopleStream } from "@/react/components/common/queries/queries";
import {
   DynamicTokenEditor,
   type DynamicTokenEditorRef,
} from "../../communications/dynamic-tokens/dynamic-token-editor";
import type { FormikProps } from "formik";
import { useGetProjectAlertInfoQuery, useGetProjectQuery } from "../project/queries";
import {
   reconstructHtmlWithTokens,
   validateAlertContent,
} from "../../communications/dynamic-tokens/token-utils";

export type Recipient = {
   id: string;
   label: string;
   first_name: string;
   last_name: string;
   can_recieve_email: boolean;
   can_recieve_sms: boolean;
   can_recieve_mobile: boolean;
};

export type AssignmentAlertFormValues = {
   additional_recipients: Recipient[];
   is_draft: boolean;
   scheduled_day: Date;
   hour: {
      id: number;
      value: number;
   };
   minute: {
      id: number;
      value: number;
   };
   day_night: {
      id: string;
      value: string;
   };
};

/** Everything the form collects, handed to a wrapper's `onSave` so it can build its own API call. */
export type AssignmentAlertsFormSubmission = {
   context: AlertContext;
   values: AssignmentAlertFormValues;
   alertContent: { subject: string; content: string };
   allRecipients: Recipient[];
   removedRecipientIds: Set<string>;
};

const COLUMN_STYLES = {
   nameHeader: { width: "40%" },
   nameBody: { width: "50%" },
   iconHeader: { border: "none" },
   removeBody: { width: "30%" },
} as const;

const RecipientsSubTable = (props: {
   recipients: Recipient[];
   handleRemove: (id: string) => void;
   showMobileColumn: boolean;
}) => {
   const { recipients, handleRemove, showMobileColumn } = props;
   const I18n = useI18nContext();
   return (
      <Table.Container>
         <Table>
            <Table.Header>
               <Table.HeaderRow>
                  <Table.HeaderCell style={COLUMN_STYLES.nameHeader}>
                     {`${I18n.t("views.company.workforce_planning.time_off.name")} (${
                        recipients.length
                     })`}
                  </Table.HeaderCell>
                  <Table.HeaderCell style={COLUMN_STYLES.iconHeader}>
                     {I18n.t("views.company.workforce_planning.people.email")}
                  </Table.HeaderCell>
                  <Table.HeaderCell style={COLUMN_STYLES.iconHeader}>
                     {I18n.t("views.company.workforce_planning.communications.sms")}
                  </Table.HeaderCell>
                  {showMobileColumn && (
                     <Table.HeaderCell style={COLUMN_STYLES.iconHeader}>
                        {I18n.t("views.company.workforce_planning.communications.mobile")}
                     </Table.HeaderCell>
                  )}
                  {/* The blank column cell below will be for a "Remove" button in the table body */}
                  <Table.HeaderCell style={COLUMN_STYLES.iconHeader} />
               </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
               {recipients.map((recipient: Recipient) => (
                  <Table.BodyRow key={`recipient-${recipient.id}`}>
                     <Table.BodyCell style={COLUMN_STYLES.nameBody}>
                        <Table.TextCell>
                           <P>{recipient.label}</P>
                        </Table.TextCell>
                     </Table.BodyCell>
                     <Table.BodyCell>
                        <Flex justifyContent="center">
                           <Table.IconCell>
                              {recipient.can_recieve_email ? <Check /> : <Ban />}
                           </Table.IconCell>
                        </Flex>
                     </Table.BodyCell>
                     <Table.BodyCell>
                        <Flex justifyContent="center">
                           <Table.IconCell>
                              {recipient.can_recieve_sms ? <Check /> : <Ban />}
                           </Table.IconCell>
                        </Flex>
                     </Table.BodyCell>
                     {showMobileColumn && (
                        <Table.BodyCell>
                           <Flex justifyContent="center">
                              <Table.IconCell>
                                 {recipient.can_recieve_mobile ? <Check /> : <Ban />}
                              </Table.IconCell>
                           </Flex>
                        </Table.BodyCell>
                     )}
                     <Table.BodyCell style={COLUMN_STYLES.removeBody}>
                        <Flex justifyContent="center">
                           <Button
                              variant="secondary"
                              onClick={() => handleRemove(recipient.id)}
                              aria-label={`${I18n.t("views.company.workforce_planning.remove")} ${
                                 recipient.label
                              }`}
                           >
                              {I18n.t("views.company.workforce_planning.remove")}
                           </Button>
                        </Flex>
                     </Table.BodyCell>
                  </Table.BodyRow>
               ))}
            </Table.Body>
         </Table>
      </Table.Container>
   );
};

const RecipientsTable = (props: {
   defaultRecipients: Recipient[];
   assignees: Recipient[];
   loading: boolean;
   handleRemove: (id: string) => void;
}) => {
   const { defaultRecipients, assignees, loading, handleRemove } = props;
   const I18n = useI18nContext();
   return (
      <Spinner loading={loading}>
         <Box marginBottom="sm">
            <Typography as="p" italic>
               {I18n.t("views.company.workforce_planning.communications.profile_settings_notice")}
            </Typography>
         </Box>
         {defaultRecipients.length > 0 && (
            <Box marginBottom="md">
               <Box marginBottom="xs">
                  <H3>
                     {`${I18n.t(
                        "views.company.workforce_planning.communications.default_recipients",
                     )}:`}
                  </H3>
               </Box>
               <RecipientsSubTable
                  recipients={defaultRecipients}
                  handleRemove={handleRemove}
                  showMobileColumn={false}
               />
            </Box>
         )}
         <Box>
            <Box marginBottom="xs">
               <H3>{`${I18n.t("views.company.workforce_planning.communications.assignees")}:`}</H3>
            </Box>
            <RecipientsSubTable
               recipients={assignees}
               handleRemove={handleRemove}
               showMobileColumn={true}
            />
         </Box>
      </Spinner>
   );
};

export type AssignmentAlertsFormProps = {
   projectId: string;
   // Recipients derived from these assignments (create flow); empty in the edit flow.
   assignments?: Array<{ id: string; recipientIds: string[] }>;
   // When set, fetches the project's alert template to seed the editor (create flow).
   alertType?: CannedMessageType;
   // Whether the project's default recipients are folded into the recipient list (create flow).
   includeProjectDefaults?: boolean;
   // Pre-seed the editor content (edit flow); create flow leaves this undefined and uses the template.
   initialContent?: { subject: string; content: string };
   // Pre-seed the recipient list (edit flow).
   initialRecipients?: Recipient[];
   // Pre-seed the schedule controls (edit flow).
   initialSchedule?: {
      scheduled_day?: Date;
      hour?: { id: number; value: number };
      minute?: { id: number; value: number };
      day_night?: { id: string; value: string };
   };
   // Whether the schedule-delivery toggle starts on (edit flow, when editing a scheduled alert).
   scheduledByDefault?: boolean;
   onClose: () => void;
   // The wrapper owns the actual API call (create vs update); the form just hands it the collected data.
   onSave: (submission: AssignmentAlertsFormSubmission) => Promise<void>;
};

function focusFirstInvalidControl() {
   setTimeout(() => {
      const firstInvalid = document.querySelector<HTMLElement>('[aria-invalid="true"], :invalid');
      if (firstInvalid) {
         firstInvalid.focus();
      } else {
         document.querySelector("main")?.scrollTo({ top: 0 });
      }
   }, 0);
}

/**
 * Shared presentational form for the assignment-alert tearsheets. Owns the recipients table,
 * schedule controls, and content editor plus their interaction state; the create and edit
 * wrappers only supply initial data and an `onSave` that performs their respective API call.
 */
export const AssignmentAlertsForm = ({
   projectId,
   assignments = [],
   alertType,
   includeProjectDefaults = false,
   initialContent,
   initialRecipients = [],
   initialSchedule,
   scheduledByDefault = false,
   onClose,
   onSave,
}: AssignmentAlertsFormProps) => {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const [shouldScheduleDelivery, setShouldScheduleDelivery] =
      useState<boolean>(scheduledByDefault);
   const [addedRecipients, setAddedRecipients] = useState<Recipient[]>(initialRecipients);
   const [removedRecipientIds, setRemovedRecipientIds] = useState<Set<string>>(new Set());
   const [recipientSearch, setRecipientSearch] = useState("");
   const [alertContent, setAlertContent] = useState({
      subject: initialContent?.subject ?? "",
      content: initialContent?.content ?? "",
   });
   const [errorFields, setErrorFields] = useState<string[]>([]);
   const editorRef = useRef<DynamicTokenEditorRef>(null);

   const { data: projectData, isLoading: projectLoading } = useGetProjectQuery(projectId);
   const defaultRecipientIds = projectData?.data?.default_recipient_ids ?? [];
   const defaultRecipientIdSet = useMemo(() => new Set(defaultRecipientIds), [defaultRecipientIds]);

   // Gate on project loading so the people query fires exactly once with the full recipient list.
   // Only the create flow folds in the project defaults; the edit flow seeds recipients from the
   // alert itself, so pulling defaults here would silently re-add ones the user had removed.
   const allPersonIds = projectLoading
      ? []
      : [
           ...new Set([
              ...assignments.flatMap((a) => a.recipientIds),
              ...(includeProjectDefaults ? defaultRecipientIds : []),
           ]),
        ];

   const { data: initialRecipientsData, isLoading: initialRecipientsLoading } =
      useGetPeopleAlertConfig(allPersonIds);

   // Derive the working recipient list from the query result, then overlay user mutations
   // additions from the dropdown and removals tracked in `removedRecipientIds`.
   const allRecipients = useMemo<Recipient[]>(() => {
      const fetched: Recipient[] = (initialRecipientsData?.data ?? []).map(
         (recipient: Recipient) => ({
            ...recipient,
            label: `${recipient.first_name} ${recipient.last_name}`,
         }),
      );
      const seen = new Set<string>();
      const combined: Recipient[] = [];
      for (const recipient of [...fetched, ...addedRecipients]) {
         if (seen.has(recipient.id) || removedRecipientIds.has(recipient.id)) continue;
         seen.add(recipient.id);
         combined.push(recipient);
      }
      return combined;
   }, [initialRecipientsData, addedRecipients, removedRecipientIds]);

   // Partition recipients into project-level defaults vs assignees (incl. dropdown additions).
   const { defaultRecipients, assignees } = useMemo(() => {
      const defaults: Recipient[] = [];
      const rest: Recipient[] = [];
      for (const recipient of allRecipients) {
         if (defaultRecipientIdSet.has(recipient.id)) {
            defaults.push(recipient);
         } else {
            rest.push(recipient);
         }
      }
      return { defaultRecipients: defaults, assignees: rest };
   }, [allRecipients, defaultRecipientIdSet]);
   const { data: peopleOptions, isLoading: peopleOptionsLoading } = useGetPeopleStream(
      groupId != "my-groups" ? groupId : undefined,
   );
   const filteredPeopleOptions = peopleOptions?.filter((option) =>
      option.label.toLowerCase().includes(recipientSearch.trim().toLowerCase()),
   );

   const { data: initialAlertContent } = useGetProjectAlertInfoQuery(projectId, alertType, {
      select: (projectAlertInfo) => {
         const template = projectAlertInfo?.data?.template;
         if (!template) return { subject: "", content: "" };
         return {
            subject: reconstructHtmlWithTokens(template.dynamic_tokens, template.subject ?? ""),
            content: reconstructHtmlWithTokens(template.dynamic_tokens, template.content ?? ""),
         };
      },
   });

   useEffect(() => {
      if (initialAlertContent && (initialAlertContent.subject || initialAlertContent.content)) {
         setAlertContent(initialAlertContent);
      }
   }, [initialAlertContent]);

   async function submitWithContext(values: AssignmentAlertFormValues, context: AlertContext) {
      const newErrorFields = validateAlertContent(alertContent);
      if (newErrorFields.length > 0) {
         setErrorFields(newErrorFields);
         focusFirstInvalidControl();
         return;
      }
      await onSave({ context, values, alertContent, allRecipients, removedRecipientIds });
   }

   function handleSubmit(values: AssignmentAlertFormValues) {
      return submitWithContext(
         values,
         shouldScheduleDelivery ? AlertContext.SCHEDULED : AlertContext.OPEN,
      );
   }

   function handleSaveDraft(values: AssignmentAlertFormValues) {
      return submitWithContext(values, AlertContext.DRAFTS);
   }

   const handleRemoveRecipient = (id: string) => {
      setRemovedRecipientIds((prev) => {
         if (prev.has(id)) return prev;
         const next = new Set(prev);
         next.add(id);
         return next;
      });
      setAddedRecipients((prev) => prev.filter((r) => r.id !== id));
   };

   const handleAddRecipient = (personId: string) => {
      const selected = peopleOptions?.find((o) => o.id === personId);
      if (!selected) return;
      setAddedRecipients((prev) =>
         prev.some((r) => r.id === selected.id) ? prev : [...prev, selected],
      );
      setRemovedRecipientIds((prev) => {
         if (!prev.has(selected.id)) return prev;
         const next = new Set(prev);
         next.delete(selected.id);
         return next;
      });
   };

   return (
      <Page>
         <Page.Main
            className="tearsheet-main"
            style={{
               display: "flex",
               flexDirection: "column",
            }}
         >
            <Page.Header>
               <Page.Title>
                  <H1>
                     {I18n.t("views.company.workforce_planning.communications.assignment_alert")}
                  </H1>
               </Page.Title>
            </Page.Header>
            <Form
               view="create"
               onSubmit={handleSubmit}
               initialValues={{
                  additional_recipients: [],
                  is_draft: false,
                  scheduled_day: initialSchedule?.scheduled_day ?? new Date(),
                  hour: initialSchedule?.hour ?? {
                     id: 12,
                     value: 12,
                  },
                  minute: initialSchedule?.minute ?? {
                     id: 0,
                     value: 0,
                  },
                  day_night: initialSchedule?.day_night ?? {
                     id: "am",
                     value: "am",
                  },
               }}
            >
               {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
               {({ isSubmitting, submitForm, values }: FormikProps<any>) => (
                  <>
                     <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                        <Card>
                           <Box padding="md" style={{ paddingBottom: "8px" }}>
                              <H2>
                                 {I18n.t(
                                    "views.company.workforce_planning.communications.recipients",
                                 )}
                              </H2>
                              <Form.Form>
                                 <Form.Row>
                                    <Select
                                       aria-label={I18n.t(
                                          "views.company.workforce_planning.communications.select_person_to_add",
                                       )}
                                       placeholder={I18n.t(
                                          "views.company.workforce_planning.communications.select_person_to_add",
                                       )}
                                       loading={peopleOptionsLoading}
                                       onSearch={(e) => setRecipientSearch(e.target.value)}
                                       onSelect={(value: any) => handleAddRecipient(value.item)}
                                    >
                                       {filteredPeopleOptions?.map((option) => (
                                          <Select.Option
                                             key={option.id}
                                             value={option.id}
                                             selected={allRecipients.some(
                                                (r) => r.id === option.id,
                                             )}
                                          >
                                             {option.label}
                                          </Select.Option>
                                       ))}
                                    </Select>
                                 </Form.Row>
                                 <RecipientsTable
                                    defaultRecipients={defaultRecipients}
                                    assignees={assignees}
                                    loading={projectLoading || initialRecipientsLoading}
                                    handleRemove={handleRemoveRecipient}
                                 />
                                 <Flex gap="sm" paddingTop="md">
                                    <Switch
                                       aria-label={I18n.t(
                                          "views.company.workforce_planning.communications.schedule_delivery",
                                       )}
                                       checked={shouldScheduleDelivery}
                                       onChange={() =>
                                          setShouldScheduleDelivery(!shouldScheduleDelivery)
                                       }
                                    />
                                    <P>
                                       {I18n.t(
                                          "views.company.workforce_planning.communications.schedule_delivery",
                                       )}
                                    </P>
                                 </Flex>
                                 {shouldScheduleDelivery && (
                                    <Form.Row>
                                       <Form.DateSelect
                                          colStart={1}
                                          name="scheduled_day"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.date",
                                          )}
                                          disabledDate={(date, today) => isBefore(date, today)}
                                          required
                                       />
                                       <Form.Select
                                          colStart={4}
                                          colWidth={3}
                                          name="hour"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.hour",
                                          )}
                                          options={scheduleAlertHourOptions}
                                          onSearch={false}
                                          required
                                       />
                                       <Form.Select
                                          colStart={7}
                                          colWidth={3}
                                          name="minute"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.minute",
                                          )}
                                          options={scheduleAlertMinuteOptions}
                                          onSearch={false}
                                          required
                                       />
                                       <Form.RadioButtons
                                          className="border-box flex"
                                          colStart={10}
                                          colWidth={2}
                                          name="day_night"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.day_night",
                                          )}
                                          tooltip={I18n.t(
                                             "views.company.workforce_planning.communications.day_night_tooltip",
                                          )}
                                          options={[
                                             {
                                                id: "am",
                                                label: "AM",
                                                value: "am",
                                             },
                                             {
                                                id: "pm",
                                                label: "PM",
                                                value: "pm",
                                             },
                                          ]}
                                       />
                                    </Form.Row>
                                 )}
                              </Form.Form>
                           </Box>
                        </Card>
                        <Box paddingTop="md">
                           <DynamicTokenEditor
                              ref={editorRef}
                              tokens={ProjectDetailAlertTokens}
                              I18n={I18n}
                              onContentChange={setAlertContent}
                              initialContent={
                                 initialContent ??
                                 initialAlertContent ?? { subject: "", content: "" }
                              }
                              errorFields={errorFields}
                           />
                        </Box>
                     </Page.Body>
                     <Page.Footer>
                        <Box padding="md">
                           <Flex justifyContent="space-between" alignItems="center">
                              <Required showLabel />
                              <FlexList space="sm">
                                 <Button
                                    variant="tertiary"
                                    disabled={isSubmitting}
                                    onClick={onClose}
                                 >
                                    {I18n.t("views.company.workforce_planning.cancel")}
                                 </Button>
                                 <Button
                                    variant="secondary"
                                    onClick={() => handleSaveDraft(values)}
                                 >
                                    {I18n.t(
                                       "views.company.workforce_planning.communications.save_draft",
                                    )}
                                 </Button>
                                 <Button type="submit" disabled={isSubmitting} onClick={submitForm}>
                                    {shouldScheduleDelivery
                                       ? I18n.t(
                                            "views.company.workforce_planning.communications.schedule",
                                         )
                                       : I18n.t(
                                            "views.company.workforce_planning.communications.send",
                                         )}
                                 </Button>
                              </FlexList>
                           </Flex>
                        </Box>
                     </Page.Footer>
                  </>
               )}
            </Form>
         </Page.Main>
      </Page>
   );
};
